import omit from 'lodash/omit';

import { withBrandColorsFallbacks } from '../clientPanelDesing/components/BackgroundSettings';

export type AppMode = {
  override?: boolean;
  catering?: string;
  shop?: string;
  [field: string]: unknown;
};

type AppearanceModule = { override?: boolean; [field: string]: unknown };

export type AppearanceModuleName = 'BrandColors' | 'OrderFormBanner';

export type AppearanceModules = {
  BrandColors: AppearanceModule | null;
  OrderFormBanner: AppearanceModule | null;
};

export type ModulesConfiguration = {
  AppMode?: AppMode;
  BrandColors?: AppearanceModule;
  OrderFormBanner?: AppearanceModule;
};

export const isShopOnlyAppMode = (mode?: AppMode | null): boolean =>
  mode?.catering === 'DISABLED' && mode?.shop === 'DISHES';

export const getEffectiveAppMode = (
  keyAppMode: AppMode | null,
  brandAppMode: AppMode
): AppMode => (keyAppMode?.override ? keyAppMode : brandAppMode);

export const buildAppearanceState = (
  keyModules: ModulesConfiguration,
  brandModules: ModulesConfiguration
): {
  hasAppearanceOverride: boolean;
  brandAppearance: AppearanceModules;
  keyAppearance: AppearanceModules;
} => {
  const hasAppearanceOverride = Boolean(
    keyModules.BrandColors?.override || keyModules.OrderFormBanner?.override
  );

  const brandAppearance: AppearanceModules = {
    BrandColors: withBrandColorsFallbacks(brandModules.BrandColors),
    OrderFormBanner: brandModules.OrderFormBanner ?? {},
  };
  const fallbackKeyModule = (moduleName: AppearanceModuleName) =>
    hasAppearanceOverride ? brandAppearance[moduleName] : null;

  return {
    hasAppearanceOverride,
    brandAppearance,
    keyAppearance: {
      BrandColors: keyModules.BrandColors
        ? withBrandColorsFallbacks(keyModules.BrandColors)
        : fallbackKeyModule('BrandColors'),
      OrderFormBanner:
        keyModules.OrderFormBanner ?? fallbackKeyModule('OrderFormBanner'),
    },
  };
};

export const shouldPrefillAppearance = (
  isAppearanceOverridden: boolean,
  isAppearancePrefilled: boolean
): boolean => !isAppearanceOverridden && !isAppearancePrefilled;

export const getOverriddenAppearanceModules = (
  keyAppearance: AppearanceModules,
  isAppearanceOverridden: boolean
): { BrandColors?: AppearanceModule; OrderFormBanner?: AppearanceModule } =>
  Object.entries(keyAppearance).reduce(
    (overriddenModules, [moduleName, module]) =>
      module
        ? {
            ...overriddenModules,
            [moduleName]: {
              ...omit(module, ['@id', '@type', '@resources']),
              override: isAppearanceOverridden,
            },
          }
        : overriddenModules,
    {}
  );

export const buildAccessKeyPayload = ({
  name,
  domain,
  appMode,
  keyAppearance,
  isAppearanceOverridden,
  isAppearanceAvailable,
}: {
  name: string;
  domain: string;
  appMode: AppMode | null;
  keyAppearance: AppearanceModules;
  isAppearanceOverridden: boolean;
  isAppearanceAvailable: boolean;
}) => ({
  name,
  domain,
  overriddenConfiguration: {
    AppMode: {
      ...appMode,
    },
    ...getOverriddenAppearanceModules(
      keyAppearance,
      isAppearanceOverridden && isAppearanceAvailable
    ),
  },
});
